import React, { useRef } from "react";
import { Building2, QrCode, Banknote, Upload, X } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { id: "transfer", label: "Bank Transfer", icon: Building2 },
  { id: "qris",     label: "QRIS",          icon: QrCode },
  { id: "cash",     label: "Cash",          icon: Banknote },
];

/**
 * Self-contained Payment Setup section used by CreateMatch.
 * Props:
 *   form, update(key, value), handleTotal(v), handlePerPlayer(v)
 * (kept "controlled by parent" so existing form-submission code is untouched)
 */
export default function PaymentSetupSection({ form, update, handleTotal, handlePerPlayer }) {
  const qrisRef = useRef(null);

  const handleQris = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File harus gambar"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("File terlalu besar"); return; }
    try {
      const data = await compressImage(file);
      update("qris_image", data);
    } catch (err) {
      console.warn("[payment-setup] image compress failed:", err?.message);
      toast.error("Gagal proses gambar");
    }
  };

  return (
    <div className="space-y-4 fade-up">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Total Match (Rp)">
          <input
            data-testid="create-total-cost"
            type="number" min={0} step={10000}
            value={form.total_cost || ""}
            onChange={(e) => handleTotal(e.target.value)}
            placeholder="500000"
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary"
          />
        </Field>
        <Field label="Per Pemain (Rp)">
          <input
            data-testid="create-cost-per"
            type="number" min={0} step={5000}
            value={form.cost_per_player || ""}
            onChange={(e) => handlePerPlayer(e.target.value)}
            placeholder="50000"
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary"
          />
        </Field>
      </div>
      <div className="text-[10px] text-muted-foreground -mt-2 px-1">
        Isi salah satu, sistem auto-hitung yang lain (berdasarkan {form.max_players} pemain). Kosongkan kalau gratis.
      </div>

      <Field label="Metode Bayar">
        <div className="grid grid-cols-3 gap-2">
          {METHODS.map((m) => {
            const Icon = m.icon;
            const active = form.payment_method === m.id;
            return (
              <button
                key={m.id}
                data-testid={`payment-method-${m.id}`}
                onClick={() => update("payment_method", m.id)}
                className={`flex flex-col items-center gap-1 py-3 rounded-2xl border transition-all ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary text-foreground"}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[11px] font-bold">{m.label}</span>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label={
        form.payment_method === "transfer" ? "Rekening / Catatan" :
        form.payment_method === "qris"     ? "Catatan QRIS (optional)" :
                                              "Catatan Cash"
      }>
        <textarea
          data-testid="payment-notes"
          rows={3}
          value={form.payment_notes}
          onChange={(e) => update("payment_notes", e.target.value)}
          placeholder={
            form.payment_method === "transfer" ? "Misal: BCA 1234567890 a.n. Budi" :
            form.payment_method === "qris"     ? "Scan QR di bawah, kirim bukti ke chat" :
                                                  "Bayar cash di lokasi sebelum main"
          }
          className="w-full bg-secondary border border-border rounded-2xl px-4 py-3 focus:outline-none focus:border-primary resize-none"
        />
      </Field>

      {form.payment_method === "qris" && (
        <Field label="QRIS Image (optional)">
          {form.qris_image ? (
            <div className="relative rounded-2xl overflow-hidden border border-border">
              <img src={form.qris_image} alt="QRIS" className="w-full max-h-56 object-contain bg-white" />
              <button onClick={() => update("qris_image", null)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 flex items-center justify-center" data-testid="remove-qris">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => qrisRef.current?.click()}
              data-testid="pick-qris"
              className="w-full border-2 border-dashed border-border rounded-2xl py-6 flex flex-col items-center gap-1.5 hover:border-primary/40"
            >
              <Upload className="h-6 w-6 text-primary" />
              <span className="text-sm font-bold">Upload gambar QRIS</span>
            </button>
          )}
          <input ref={qrisRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleQris(e.target.files?.[0])} data-testid="qris-file-input" />
        </Field>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Deadline Tanggal">
          <input data-testid="deadline-date" type="date" value={form.payment_deadline_date} onChange={(e) => update("payment_deadline_date", e.target.value)} className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
        </Field>
        <Field label="Deadline Jam">
          <input data-testid="deadline-time" type="time" value={form.payment_deadline_time} onChange={(e) => update("payment_deadline_time", e.target.value)} className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

async function compressImage(file) {
  const img = await fileToImage(file);
  const maxDim = 1024;
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
    else { width = Math.round((width * maxDim) / height); height = maxDim; }
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.85);
}

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img); img.onerror = reject; img.src = reader.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}
